import { useEffect, useRef, useState } from 'react';
import { SigningAvatar, describeProvenance, type MotionProvenance } from '../../avatar';
import type { LetterMark } from '../lib/practice';
import type { PracticePhase } from '../state/practice';

interface PracticeStageProps {
  word: string;
  phase: PracticePhase;
  /** Time for the whole word — one step per letter. */
  cycleMs: number;
  /** Bumped when spelling (re)starts, so the same word replays. */
  run: number;
  /** Shown only in feedback, marked per letter. Null conceals the word. */
  marks: LetterMark[] | null;
  /** Told when the 3D stage is unavailable, so the drill can say so. */
  onRenderingChange?(rendering: boolean): void;
}

/**
 * The drill's stage. Same avatar as the chat stage, opposite disclosure: while
 * the hand is spelling, nothing on screen may name the letters — the word is
 * the test. Dots fill as letters land so the learner can keep place; the
 * letters themselves appear only with the feedback marks.
 */
export function PracticeStage({
  word,
  phase,
  cycleMs,
  run,
  marks,
  onRenderingChange,
}: PracticeStageProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const avatar = useRef<SigningAvatar | null>(null);
  const [rendering, setRendering] = useState(false);
  const [provenance, setProvenance] = useState<MotionProvenance | null>(null);
  const [signedCount, setSignedCount] = useState(0);

  const signing = phase === 'signing';

  useEffect(() => {
    if (!canvas.current) return;
    const instance = SigningAvatar.create({
      canvas: canvas.current,
      onProvenance: (kind) => {
        setProvenance(kind);
        setSignedCount((count) => count + 1);
      },
    });
    avatar.current = instance;
    setRendering(Boolean(instance));
    onRenderingChange?.(Boolean(instance));

    return () => {
      instance?.dispose();
      avatar.current = null;
    };
  }, [onRenderingChange]);

  useEffect(() => {
    if (!word) return;
    setSignedCount(0);
    // Paused, the avatar holds a token's final form — which here would be the
    // word's last letter, sitting on screen throughout the ask. So the stage
    // only carries the word while it is actually spelling it; otherwise it
    // rests, and the reveal is the feedback's job alone.
    const next = avatar.current?.setToken(signing ? `fs-${word}` : '', cycleMs, signing);
    if (next) setProvenance(next);
  }, [word, run, cycleMs, signing]);

  useEffect(() => {
    avatar.current?.setPlaying(signing);
  }, [signing]);

  // The stage must not leak the answer, in the accessible name either.
  const description = !word
    ? 'Signing stage, resting.'
    : marks
      ? `Signing stage. The word was ${word}.`
      : `Signing stage: fingerspelling a ${word.length} letter word.`;

  return (
    <div className="stage-frame" data-rendering={rendering} role="img" aria-label={description}>
      <span className="crop-mark" data-corner="tl" />
      <span className="crop-mark" data-corner="tr" />
      <span className="crop-mark" data-corner="bl" />
      <span className="crop-mark" data-corner="br" />

      <canvas className="stage-canvas" ref={canvas} aria-hidden="true" />

      <p className="stage-caption" data-unverified={provenance ? provenance.kind !== 'reviewed' : true}>
        {provenance ? describeProvenance(provenance) : 'Avatar stage'}
      </p>
      {word && (
        <p className="stage-counter">
          {signing ? `LETTER ${Math.min(signedCount, word.length)} / ${word.length}` : `${word.length} LETTERS`}
        </p>
      )}
    </div>
  );
}
